import { EventEmitter } from 'events';
import { hubConnection } from 'signalr-no-jquery';
import { STORAGE_KEYS } from '../../idscan/configuration/StorageKeys';
import { ScannerError, ScannerState, ScanProcess } from '../../idscan/enums/Scanner';
import { IScannerState } from '../../idscan/interfaces/ScannerState';
import StorageService from './StorageService';
import { osName, osVersion, browserName, fullBrowserVersion, mobileVendor, mobileModel, isMobile } from 'mobile-device-detect';
import JourneyManager from '../../idscan/classes/JourneyManager';
import { Mode } from '../../idscan/enums/Mode';

//global version string
declare var VERSION: string;

/**
 * @internal
 */
export enum SocketEvents {
	RESULT = 'result',
	UPDATE = 'update',
}

/**
 * @internal
 */
enum HubEvents {
	TERMINAL_STATUS_UPDATED = 'terminalStatusUpdated',
	BATCH_SCAN_WORKFLOW_SCAN_STARTED = 'batchScanWorkflowScanStarted',
	BATCH_SCAN_WORKFLOW_SCAN_FINISHED = 'batchScanWorkflowScanFinished',
	BATCH_SCAN_WORKFLOW_PROCESSING_FINISHED = 'batchScanWorkflowProcessingFinished',
	IMAGE_SOURCE_BUSY = 'imageSourceBusy',
	IMAGE_SOURCE_CONNECTED = 'imageSourceConnected',
	IMAGE_SOURCE_DISCONNECTED = 'imageSourceDisconnected',
}

/**
 * @internal
 */
enum HubActions {
	RELEASE_TERMINAL = 'releaseTerminal',
	REGISTER_AND_OCCUPY_SCANNER = 'registerAndOccupyScanner',
	RESET = 'reset',
	SET_SCANNER_TRIGGER_MODE = 'setScannerTriggerMode',
}

/**
 * @internal
 */
export class SocketService extends EventEmitter {

	public get state(): number {
		return this._state;
	}

	private _connection?: any;
	private _state: number = 0;
	private _hub?: any;

	private _socketUrl?: string;
	private _logging?: boolean;

	private _journeyDefinitionId: string = '';
	private _journeyId: string | undefined;

	private _scannerState: IScannerState = {
		connected: false,
		error: undefined,
		scanProcess: ScanProcess.EMPTY,
		state: undefined
	};

	private _oldScannerState: IScannerState = {
		connected: false,
		error: undefined,
		scanProcess: ScanProcess.EMPTY,
		state: undefined
	};

	public configure = (socketUrl: string | undefined = this._socketUrl, logging: boolean = false): Promise<void> => new Promise((resolve) => {
		this._socketUrl = socketUrl ? socketUrl : this._socketUrl;
		this._logging = logging;

		return resolve();
	})

	public initialise = (): Promise<void> => new Promise((resolve) => {
		this.removeAllListeners();

		this.clearListeners();

		this._connection = hubConnection(this._socketUrl, {
			logging: this._logging,
			useDefaultPath: false
		});

		this._connection.qs = `Authorization=${StorageService.get(STORAGE_KEYS.defaultToken)}`;

		this._hub = this._connection.createHubProxy('signalRMethodCallHub');

		this.setListeners();

		return resolve();
	})

	/**
	 * Release the terminal
	 */
	public release = (): Promise<void> => new Promise((resolve) => {
		if (this._hub && this._state === 1) {
			return this._hub.invoke(HubActions.RELEASE_TERMINAL)
				.then(() => {
					this._scannerState = {
						connected: false,
						error: ScannerError.IS_RELEASED,
						scanProcess: ScanProcess.EMPTY,
						state: undefined
					};
				})
				.then(this.pushState)
				.then(resolve);
		} else {
			return resolve();
		}
	})

	/**
	 * Assign the state of the signalR connection to a variable
	 * @param change
	 */
	public stateChanged = (change: any) => {
		/*
			0: Connecting
			1: Connected
			2: Reconnecting
			4: Disconnected
		*/
		this._state = change.newState;
	}

	/**
	 * Start a new connection and subscribe to events
	 */
	public start = (journeyDefinitionId: string, journeyId: string): Promise<IScannerState> => {
		return new Promise((resolve, reject) => {
			if (this._connection) {
				this._journeyDefinitionId = journeyDefinitionId;
				this._journeyId = journeyId;
				return this._connection.start()
					.done(() => {
						this.tryRegistering()
							.then(() => resolve(this._scannerState));
					})
					.fail(() => reject({
						connected: false,
						error: `Cannot reach the server`,
						scanProcess: ScanProcess.EMPTY,
						state: undefined
					}));
			} else {
				return reject();
			}
		});
	}

	public clearListeners = () => {
		const { _hub } = this;

		if (_hub) {
			_hub.off(HubEvents.TERMINAL_STATUS_UPDATED);
			_hub.off(HubEvents.BATCH_SCAN_WORKFLOW_SCAN_STARTED);
			_hub.off(HubEvents.BATCH_SCAN_WORKFLOW_SCAN_FINISHED);
			_hub.off(HubEvents.BATCH_SCAN_WORKFLOW_PROCESSING_FINISHED);
			_hub.off(HubEvents.IMAGE_SOURCE_BUSY);
			_hub.off(HubEvents.IMAGE_SOURCE_CONNECTED);
			_hub.off(HubEvents.IMAGE_SOURCE_DISCONNECTED);
		}
	}

	/**
	 * Set listeners for the signalR hub
	 */
	public setListeners = () => {
		const { _connection, _hub } = this;

		if (_connection) {
			_connection.stateChanged(this.stateChanged);

			_connection.error((error: any) => {
				console.warn(error);
				this.setSourceState(ScannerState.DISCONNECTED);
			});
		}

		if (_hub) {
			_hub.on(HubEvents.TERMINAL_STATUS_UPDATED, this.setState);

			_hub.on(HubEvents.BATCH_SCAN_WORKFLOW_SCAN_STARTED, () => {
				this.setScanProcess(ScanProcess.SCANNING);
			});

			_hub.on(HubEvents.BATCH_SCAN_WORKFLOW_SCAN_FINISHED, () => {
				this.setScanProcess(ScanProcess.PROCESSING);
			});

			_hub.on(HubEvents.BATCH_SCAN_WORKFLOW_PROCESSING_FINISHED, (result: any) => {
				this.setScanProcess(ScanProcess.EMPTY)
					.then(this.turnOffAutoTriggerMode)
					.then(() => this.emit(SocketEvents.RESULT, result));
			});

			_hub.on(HubEvents.IMAGE_SOURCE_BUSY, () => {
				this.setSourceState(ScannerState.BUSY);
			});

			_hub.on(HubEvents.IMAGE_SOURCE_CONNECTED, () => {
				this.setSourceState(ScannerState.CONNECTED);
			});

			_hub.on(HubEvents.IMAGE_SOURCE_DISCONNECTED, () => {
				this.setSourceState(ScannerState.DISCONNECTED);
			});
		}
	}

	/**
	 * Stop the connection
	 */
	public stop = (): Promise<boolean> => new Promise((resolve) => {
		this.release()
			.then(() => this._connection ? this._connection.stop() : undefined)
			.then(() => resolve(true));
	})

	/**
	 * Try registering the scanner
	 */
	public tryRegistering = (platform: string | undefined = this._journeyDefinitionId, journeyId: string | undefined = this._journeyId) => new Promise((resolve, reject) => {

		// will send MetaData for first request(Identity front)
		let metaData: any[] = [];
		if (JourneyManager.mode === Mode.WEB) {
			metaData = [{
				name: 'Channel', // Mobile, Web, or Capture Studio
				value: 'Web',
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Web Sdk Version', // Version Number of WEB SDK
				value: VERSION,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Platform', // Windows, MacOS, Android, iOS, Linux, etc
				value: osName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Platform Version', // The version number of the above-mentioned platform
				value: osVersion,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser', // Chrome, Safari, etc
				value: browserName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser Version', // Browser version
				value: fullBrowserVersion,
				step: 'GENERAL',
				type: 'Simple'
			}];
		} else if (JourneyManager.mode === Mode.CAPTURESTUDIO) {
			metaData = [{
				name: 'Platform', // Windows, MacOS, Android, iOS, Linux, etc
				value: osName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Platform Version', // The version number of the above-mentioned platform
				value: osVersion,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser', // Chrome, Safari, etc
				value: browserName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser Version', // Browser version
				value: fullBrowserVersion,
				step: 'GENERAL',
				type: 'Simple'
			}];
		}

		if (this._hub) {
			return this._hub.invoke(HubActions.REGISTER_AND_OCCUPY_SCANNER, { platform, deviceId: '0', metaData, journeyId })
				.then(this.setState)
				.then(resolve);
		} else {
			return reject();
		}
	})

	public setScannerTriggerMode = (state: string | undefined) => new Promise((resolve, reject) => {
		if (this._hub) {
			return this._hub.invoke(HubActions.SET_SCANNER_TRIGGER_MODE, 'trigger', state)
				.then(resolve);
		} else {
			return reject();
		}
	})

	public resetTerminal = () => new Promise((resolve, reject) => {
		if (this._hub && this._state !== 4) {
			return this._hub.invoke(HubActions.RESET).then(resolve);
		} else {
			return reject();
		}
	})

	/**
	 * Set scan process
	 * @param process
	 */
	private setScanProcess = (process: ScanProcess) => {
		this._scannerState.scanProcess = process;

		return this.pushState();
	}

	/**
	 * Set source state to be either busy, connected, or disconnected
	 * @param sourceState
	 */
	private setSourceState = (sourceState: ScannerState) => {
		this._scannerState.state = sourceState;
		this.pushState();
	}

	/**
	 * Set state of the connection and push the change
	 * @param state
	 */
	private setState = (state: any): void => {
		this._scannerState.state = this._scannerState.state;
		this._scannerState.error = state.failReason && state.failReason !== 'None' ? state.failReason : undefined;
		this._scannerState.connected = state.isOccupiedSuccessfully && state.scannerInfo.isConnected;

		if (this._scannerState.state === undefined && this._scannerState.error === undefined && this._scannerState.connected === true) {
			this._scannerState.state = ScannerState.CONNECTED;
		}

		this.pushState();
	}

	/**
	 * Check the difference and if different, publish the scan state event
	 */
	private pushState = (): Promise<void> => new Promise((resolve) => {
		if (JSON.stringify(this._scannerState) !== JSON.stringify(this._oldScannerState)) {
			this.emit(SocketEvents.UPDATE, this._scannerState);
		}

		this._oldScannerState = Object.assign({}, this._scannerState);

		return resolve();
	})

	/**
	 * Call the register method after the scan round is finished
	*/
	private turnOffAutoTriggerMode = (): Promise<void> => new Promise((resolve, reject) => {
		if (this._state !== 4) {
			return this.setScannerTriggerMode('false')
				.then(() => resolve());
		} else {
			return reject();
		}

	})

}

export default new SocketService();
